import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogClose } from '@radix-ui/react-dialog';

import { Button, Input, Spinner } from '@/components/atoms';
import { QUESTION_POPUP_TEST_IDS } from '@/constants/data-testids';
import { captureAsyncError } from '@/utils/captureAsyncError';

import { QuestionPopupData, SetQuestionPopupData } from '../QuestionPopup';

type ManageQuestionPopupProps = {
  type: 'create' | 'edit';
  questionPopupData: QuestionPopupData;
  isLoading?: boolean;
  onNextStepClick: () => Promise<void>;
  setQuestionPopupData: SetQuestionPopupData;
};

export const ManageQuestionPopup: FC<ManageQuestionPopupProps> = ({
  type,
  questionPopupData,
  isLoading,
  onNextStepClick,
  setQuestionPopupData,
}) => {
  const { t } = useTranslation('components');
  const title =
    type === 'create'
      ? t('manageQuestionPopup.buttonNewQuestion')
      : t('manageQuestionPopup.buttonEditQuestion');

  const handleChangeCategoryName = (newValue: string) => {
    setQuestionPopupData(prev => ({ ...prev, text: newValue }));
  };

  const isDisabledNextButton = !questionPopupData.text;

  return (
    <div data-testid={QUESTION_POPUP_TEST_IDS.STEP1}>
      <div className="mb-5">
        <p className="text-body-12-semibold text-custom-warm-grey-500 mb-2">
          {t('manageQuestionPopup.title')}
        </p>
        <p className="text-body-20-semibold text-custom-warm-grey-800">{title}</p>
      </div>
      <div className="mb-6">
        <Input
          value={questionPopupData.text}
          onChange={e => handleChangeCategoryName(e.target.value)}
          placeholder={t('manageQuestionPopup.inputs.questionPlaceholder')}
          hintText={t('manageQuestionPopup.hintText')}
          className="w-full"
          maxLength={500}
        />
      </div>
      <div className="flex items-center gap-3">
        <DialogClose asChild>
          <Button size="lg" variant="secondary" className="w-full">
            {t('manageQuestionPopup.cancelButton')}
          </Button>
        </DialogClose>
        <Button
          size="lg"
          variant="primary"
          className="relative w-full"
          onClick={() => {
            void Promise.resolve(onNextStepClick()).catch(captureAsyncError);
          }}
          disabled={isDisabledNextButton}
        >
          {t('manageQuestionPopup.nextStep')}{' '}
          {isLoading && <Spinner className="absolute top-1/2 -translate-y-1/2 right-8" />}
        </Button>
      </div>
    </div>
  );
};
